import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapCareTeamMemberRow } from '@/features/patient-record/api/careTeam';
import { patientQueries } from '@/features/patient-record/queries';
import type { CareTeamMemberDetailViewModel } from '@/models/patient';

/**
 * Care-team mutation hooks split per action (PTV2-1095). Each hook owns its
 * own snapshot/rollback against `patientQueries.careTeamMembers(guid)` so the
 * panel rows update instantly and revert on failure. The detail query is
 * invalidated on settle so the canonical row state (with the server-assigned
 * `id` / `addedAt` / `addedBy`) wins after the round-trip.
 *
 * Toast keys are wired through React Query's `meta.successMessage` — the
 * AppProvider toaster picks them up and translates with i18n.
 */

type Members = readonly CareTeamMemberDetailViewModel[];

interface CareTeamMutationContext {
  readonly previous: Members | undefined;
}

interface UseCareTeamCache {
  readonly queryKey: readonly unknown[];
  readonly snapshot: () => CareTeamMutationContext;
  readonly setMembers: (next: Members) => void;
  readonly rollback: (ctx: CareTeamMutationContext | undefined) => void;
  readonly settle: () => void;
}

function useCareTeamCache(guid: string): UseCareTeamCache {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.careTeamMembers(guid);
  const detailKey = patientQueries.detail(guid).queryKey;

  return {
    queryKey,
    snapshot: () => ({ previous: queryClient.getQueryData<Members>(queryKey) }),
    setMembers: (next) => {
      queryClient.setQueryData<Members>(queryKey, next);
    },
    rollback: (ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData<Members>(queryKey, ctx.previous);
    },
    settle: () => {
      // Refresh the dedicated team cache + the patient header / list views
      // that still read the legacy `assigned_gc` snapshot off the Person
      // record (panels are migrating to the new endpoint, but they will
      // co-exist until the contract consolidates).
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
      queryClient.invalidateQueries({ queryKey: detailKey }).catch(() => undefined);
      // Browse Patients surfaces program / assigned GC (list columns + its filter options), so refresh it.
      queryClient.invalidateQueries({ queryKey: ['recent-patients'] }).catch(() => undefined);
    },
  };
}

// ─── add member ─────────────────────────────────────────────────────

export interface AddCareTeamMemberInput {
  readonly userId: string;
  readonly displayName: string;
  readonly role: string;
}

export function useAddCareTeamMember(
  guid: string,
): UseMutationResult<CareTeamMemberDetailViewModel | null, Error, AddCareTeamMemberInput, CareTeamMutationContext> {
  const cache = useCareTeamCache(guid);
  return useMutation({
    meta: { successMessage: 'success.toast.title.care_team_member_added' },
    mutationFn: async (input) => {
      const dto = await apiClient.addCareTeamMember(guid, {
        user_id: input.userId,
        role: input.role,
      });
      return mapCareTeamMemberRow(dto);
    },
    onMutate: (input) => {
      const ctx = cache.snapshot();
      const optimistic: CareTeamMemberDetailViewModel = {
        id: `optimistic-${input.userId}-${String(Date.now())}`,
        userId: input.userId,
        displayName: input.displayName,
        role: input.role,
        isPrimary: false,
        addedAt: new Date().toISOString(),
        addedBy: null,
      };
      cache.setMembers([...(ctx.previous ?? []), optimistic]);
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: cache.settle,
  });
}

// ─── remove member ──────────────────────────────────────────────────

export function useRemoveCareTeamMember(
  guid: string,
): UseMutationResult<undefined, Error, { readonly memberId: string }, CareTeamMutationContext> {
  const cache = useCareTeamCache(guid);
  return useMutation({
    meta: { successMessage: 'success.toast.title.care_team_member_removed' },
    mutationFn: async ({ memberId }) => apiClient.removeCareTeamMember(guid, memberId),
    onMutate: ({ memberId }) => {
      const ctx = cache.snapshot();
      cache.setMembers((ctx.previous ?? []).filter((m) => m.id !== memberId));
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: cache.settle,
  });
}

// ─── update role ────────────────────────────────────────────────────

export function useUpdateCareTeamMemberRole(
  guid: string,
): UseMutationResult<
  CareTeamMemberDetailViewModel | null,
  Error,
  { readonly memberId: string; readonly role: string },
  CareTeamMutationContext
> {
  const cache = useCareTeamCache(guid);
  return useMutation({
    meta: { successMessage: 'success.toast.title.care_team_member_role_updated' },
    mutationFn: async ({ memberId, role }) => {
      const dto = await apiClient.updateCareTeamMemberRole(guid, memberId, { role });
      return mapCareTeamMemberRow(dto);
    },
    onMutate: ({ memberId, role }) => {
      const ctx = cache.snapshot();
      cache.setMembers(
        (ctx.previous ?? []).map((m) => (m.id === memberId ? { ...m, role } : m)),
      );
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: cache.settle,
  });
}

// ─── set / clear primary ────────────────────────────────────────────

/** `null` userId clears the primary; otherwise promotes that user. */
export function useSetPrimaryClinician(
  guid: string,
): UseMutationResult<
  CareTeamMemberDetailViewModel | null,
  Error,
  { readonly userId: string | null },
  CareTeamMutationContext
> {
  const cache = useCareTeamCache(guid);
  return useMutation({
    meta: { successMessage: 'success.toast.title.care_team_primary_updated' },
    mutationFn: async ({ userId }) => {
      if (userId === null) {
        await apiClient.clearPrimaryClinician(guid);
        return null;
      }
      const dto = await apiClient.setPrimaryClinician(guid, userId);
      return mapCareTeamMemberRow(dto);
    },
    onMutate: ({ userId }) => {
      const ctx = cache.snapshot();
      cache.setMembers(
        (ctx.previous ?? []).map((m) => ({ ...m, isPrimary: m.userId === userId })),
      );
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      cache.rollback(ctx);
    },
    onSettled: cache.settle,
  });
}
